import { brandOf } from "./domains";
import {
  CHANNELS, CORE_CHANNELS, MIN_SEARCHES_FOR_POSITION, prepareReport, rankAdvertisers, passesFilters,
  reportHeader, scopedKeywordsAndPostcodes, keywordsBySearches, highestAveragePosition, classifyUnseen,
  mean, round,
} from "./reportCore";

// Every number on the Advertiser Overview page: one advertiser in detail,
// the market only as reference rows (#1 and the top-3 average).
// Pure: no React, no database, no browser, no clock. Uses the same counting
// as marketOverview() (reportCore.js), so with the same data and filters an
// advertiser's rank and visibility are identical on both pages.
// The result shape is documented in advertiserOverview.md; the words in
// advertiserOverviewText.js.

export const ADVERTISER_OVERVIEW_VERSION = 1;

const WINDOW_SIZE = 5;
const MATCHED_BY = { domain: "website", likely: "likely", manual: "manual", name: "name" };
const GRID_FIELDS = { ppc: "ppcSeenIn", local: "localSeenIn", seo: "seoSeenIn" };

// The advertiser key for a website or listing as typed or saved:
// "https://www.x.co.uk/page" -> "x.co.uk"; "name:joesplumbing" stays as it is,
// unless that listing has been linked to a website, which it then becomes.
export function resolveAdvertiserKey(key, listingLinks = []) {
  if (!key || typeof key !== "string" || !key.trim()) return null;
  const k = key.trim();
  if (k.startsWith("name:")) {
    const link = (listingLinks || []).find(l => l && l.listing_key === k && l.action === "link" && (!l.status || l.status === "applied"));
    return (link && brandOf(link.domain)) || k;
  }
  return brandOf(k) || k.toLowerCase();
}

// The job's own business ("your business"), by its website; null if it has none.
export function yourBusinessKey(job) {
  return brandOf(job?.business_website) || null;
}

export function advertiserOverview(data, advertiserKey, filters = {}) {
  const report = prepareReport(data, filters);
  const { f, completed, of, ofByDevice, searchByKey, advertisers, stats, towns } = report;
  const businessKey = yourBusinessKey(report.job);
  const key = resolveAdvertiserKey(advertiserKey, data.listingLinks);

  // The ranking: everyone the Business type and Advertisers filters keep,
  // plus the pinned advertiser, who is never filtered out - so ticking
  // advertisers compares the pinned one against just them.
  const pool = rankAdvertisers(report, a => passesFilters(a, f) || a.key === key);
  pool.forEach(r => { delete r.visibilityAppearances; });

  const picker = pool.map(r => ({
    rank: r.rank, key: r.key, displayName: r.displayName, visibilityScore: r.visibility.score, isYourBusiness: r.key === businessKey,
    businessType: r.businessType, visibility: r.visibility,
  }));
  if (businessKey && !picker.some(p => p.key === businessKey)) {
    const own = rankAdvertisers(report, a => a.key === businessKey)[0];
    picker.unshift({
      rank: null, key: businessKey, displayName: advertisers[businessKey]?.displayName || businessKey,
      visibilityScore: own ? own.visibility.score : 0, isYourBusiness: true,
      businessType: advertisers[businessKey]?.businessType || classifyUnseen(businessKey, data.typeOverrides),
      visibility: own ? own.visibility : { score: 0, searches: 0, of },
    });
  }

  const base = { version: ADVERTISER_OVERVIEW_VERSION, ...reportHeader(report, data.samples), picker };
  if (!key) {
    return {
      ...base, pinned: null, rank: null, window: null, comparison: null,
      channels: null, devices: null, grid: [], gridSummary: null, rivals: [], facts: [],
    };
  }

  // ---- The pinned advertiser ----
  const me = pool.find(r => r.key === key) || null;
  const mine = me ? stats[key] : null;
  const known = advertisers[key];
  const isName = key.startsWith("name:");
  const businessType = known ? known.businessType : classifyUnseen(key, data.typeOverrides);
  const pinned = {
    key,
    domain: known ? known.domain : isName ? null : key,
    displayName: known ? known.displayName : key,
    matchedBy: MATCHED_BY[known ? known.matchedBy : isName ? "name" : "domain"],
    linkedListings: known ? known.linkedListings : [],
    businessType,
    isYourBusiness: key === businessKey,
    seen: Boolean(me),
    excludedByTypeFilter: f.businessTypes.length > 0 && !f.businessTypes.includes(businessType.type),
    excludedByAdvertiserFilter: f.advertisers.length > 0 && !f.advertisers.includes(key),
    firstSeenAt: me ? me.firstSeenAt : null,
    lastSeenAt: me ? me.lastSeenAt : null,
  };

  const rank = {
    position: me ? me.rank : null,
    of: pool.length,
    visibility: me ? me.visibility : { score: 0, searches: 0, of },
  };

  // ---- Where you rank: ±2 window, #1 as a reference row when not in it ----
  const window = rankWindow(pool, me);

  // ---- Comparison: #1 (or #2, "next", if you're #1) and the top 3 others ----
  const others = pool.filter(r => r.key !== key);
  const leaderRow = others[0] || null;
  const top3Rows = others.slice(0, 3);
  const comparison = {
    leader: leaderRow ? {
      role: me && me.rank === 1 ? "next" : "first",
      key: leaderRow.key, displayName: leaderRow.displayName,
      visibility: { searches: leaderRow.visibility.searches, of },
    } : null,
    top3: {
      keys: top3Rows.map(r => r.key),
      visibility: { searches: averageOf(top3Rows.map(r => r.visibility.searches)), of },
    },
  };

  // ---- Channels ----
  const deviceSearches = (k, channel, device) =>
    [...stats[k].channels[channel].keys()].filter(sk => searchByKey.get(sk)?.device === device).length;
  const channels = {};
  for (const c of CHANNELS) {
    const myChannel = me ? me.channels[c] : null;
    const inChannel = others.filter(r => r.channels[c]).sort(byChannelSearches(c));
    if (!myChannel && inChannel.length === 0) { channels[c] = null; continue; }
    const lead = inChannel[0] || null;
    const top3 = inChannel.slice(0, 3);
    channels[c] = {
      you: myChannel ? {
        searches: myChannel.searches, of, avgPosition: myChannel.avgPosition, bestPosition: myChannel.bestPosition,
        ...(c === "shopping" ? { withPrice: myChannel.withPrice } : {}),
      } : null,
      leader: lead ? { key: lead.key, displayName: lead.displayName, searches: lead.channels[c].searches, avgPosition: lead.channels[c].avgPosition } : null,
      top3: top3.length > 0 ? {
        keys: top3.map(r => r.key),
        searches: averageOf(top3.map(r => r.channels[c].searches)),
        avgPosition: averageOf(top3.map(r => r.channels[c].avgPosition).filter(p => p != null)),
      } : null,
      searchesWithChannel: completed.filter(s => s.seenChannels.has(c)).length,
      byDevice: Object.fromEntries(["mobile", "desktop"].map(d => [d, {
        you: { searches: myChannel ? deviceSearches(key, c, d) : 0, of: ofByDevice[d] },
        leader: lead ? { searches: deviceSearches(lead.key, c, d), of: ofByDevice[d] } : null,
      }])),
    };
  }

  // ---- Devices: you, the leader and the top 3 others ----
  const devices = {};
  for (const d of ["mobile", "desktop"]) {
    devices[d] = {
      you: { searches: me ? me.devices[d].searches : 0, of: ofByDevice[d] },
      leader: leaderRow ? { searches: leaderRow.devices[d].searches, of: ofByDevice[d] } : null,
      top3: top3Rows.length > 0 ? { searches: averageOf(top3Rows.map(r => r.devices[d].searches)), of: ofByDevice[d] } : null,
    };
  }

  // ---- Where you show up: keyword x postcode, plus "any keyword" per postcode ----
  const { keywords, postcodes } = scopedKeywordsAndPostcodes(report);
  const inSearch = (channel, s) => Boolean(mine) && (channel === "any" ? mine.coreSearches.has(s.key) : mine.channels[channel].has(s.key));
  const grid = [];
  for (const postcode of postcodes) {
    for (const keyword of [...keywords, null]) {
      const cell = completed.filter(s => s.postcode === postcode && (keyword === null || s.keyword === keyword));
      const seenIn = cell.filter(s => inSearch("any", s)).length;
      const searchesWithAds = cell.filter(s => s.seenChannels.has("ppc")).length;
      const row = { keyword, postcode, town: towns[postcode] || null, searches: cell.length, seenIn };
      for (const [channel, field] of Object.entries(GRID_FIELDS)) row[field] = cell.filter(s => inSearch(channel, s)).length;
      row.searchesWithAds = searchesWithAds;
      row.status = seenIn > 0 ? "seen" : searchesWithAds > 0 ? "not_seen_ads_ran" : "not_seen_no_ads";
      grid.push(row);
    }
  }
  const cells = grid.filter(g => g.keyword !== null && g.searches > 0);
  const postcodeRows = grid.filter(g => g.keyword === null && g.searches > 0);
  const gridSummary = {
    cells: cells.length,
    cellsSeen: cells.filter(g => g.status === "seen").length,
    cellsNotSeenAdsRan: cells.filter(g => g.status === "not_seen_ads_ran").length,
    postcodes: postcodeRows.length,
    postcodesSeen: postcodeRows.filter(g => g.status === "seen").length,
    keywordsBySearches: keywordsBySearches(keywords, completed),
  };

  // ---- Who you're up against: everyone sharing 1+ search (ads, map pack, organic) ----
  const rivals = mine ? others
    .map(r => ({ r, shared: [...stats[r.key].coreSearches].filter(sk => mine.coreSearches.has(sk)).length }))
    .filter(x => x.shared > 0)
    .sort((a, b) => b.shared - a.shared || a.r.rank - b.r.rank)
    .map(({ r, shared }) => ({
      key: r.key, displayName: r.displayName, businessType: r.businessType,
      sharedSearches: shared, of: mine.coreSearches.size, visibility: { score: r.visibility.score },
    })) : [];

  return {
    ...base,
    pinned,
    rank,
    window,
    comparison,
    channels,
    devices,
    grid,
    gridSummary,
    rivals,
    facts: buildFacts({ me, mine, pool, of, completed, gridSummary }),
  };
}

// Pinned 1st or 2nd -> ranks 1-5; last or second last -> the bottom 5;
// otherwise the pinned advertiser ±2. Fewer than 5 advertisers -> all.
// Not seen -> the top 5. #1 is added as a reference row whenever the window
// doesn't include it.
function rankWindow(pool, me) {
  const n = pool.length;
  let start = 0;
  if (n > WINDOW_SIZE && me) {
    const i = me.rank - 1;
    start = i <= 1 ? 0 : i >= n - 2 ? n - WINDOW_SIZE : i - 2;
  }
  const windowRow = r => ({
    rank: r.rank, key: r.key, displayName: r.displayName, businessType: r.businessType,
    visibility: r.visibility, isPinned: Boolean(me) && r.key === me.key,
  });
  const top = pool[0];
  return {
    rows: pool.slice(start, start + WINDOW_SIZE).map(windowRow),
    reference: start > 0 ? { rank: 1, key: top.key, displayName: top.displayName, businessType: top.businessType, visibility: top.visibility } : null,
  };
}

// Most searches in a channel first; ties by rank.
function byChannelSearches(channel) {
  return (a, b) => b.channels[channel].searches - a.channels[channel].searches || a.rank - b.rank;
}

// Mean to 1 decimal place; null for an empty list.
function averageOf(values) {
  return values.length > 0 ? round(mean(values), 1) : null;
}

// ---------- Facts ----------

// The only claims made about the pinned advertiser; each carries its numbers.
// Wording in advertiserOverviewText.js.
function buildFacts({ me, mine, pool, of, completed, gridSummary }) {
  const facts = [];
  if (of === 0) return facts;
  const searches = me ? me.visibility.searches : 0;
  facts.push({ claim: "appeared_in", scope: "any", value: searches, of });
  if (!me) return facts;

  const adsRan = completed.filter(s => s.seenChannels.has("ppc"));
  if (searches > 0 && adsRan.length > 0) {
    facts.push({ claim: "appeared_in", scope: "ads_ran", value: adsRan.filter(s => mine.coreSearches.has(s.key)).length, of: adsRan.length });
  }
  if (searches > 0 && gridSummary.postcodes > 0) {
    facts.push({ claim: "appeared_in", scope: "postcodes", value: gridSummary.postcodesSeen, of: gridSummary.postcodes });
  }
  for (const c of CHANNELS) {
    if (me.channels[c]) facts.push({ claim: "appeared_in", scope: c, value: me.channels[c].searches, of });
  }

  if (me.rank === 1 && searches > 0) facts.push({ claim: "seen_most_often", scope: "any", value: searches, of });
  for (const c of CHANNELS) {
    const first = pool.filter(r => r.channels[c]).sort(byChannelSearches(c))[0];
    if (first && first.key === me.key) facts.push({ claim: "seen_most_often", scope: c, value: me.channels[c].searches, of });
  }

  for (const c of CORE_CHANNELS) {
    const best = highestAveragePosition(pool, c);
    if (best && best.key === me.key) facts.push({ claim: "highest_average_position", channel: c, value: me.channels[c].avgPosition });
  }
  for (const c of CHANNELS) {
    const ch = me.channels[c];
    if (ch && ch.avgPosition != null && ch.searches >= MIN_SEARCHES_FOR_POSITION) {
      facts.push({ claim: "average_position", channel: c, value: ch.avgPosition, searches: ch.searches });
    }
  }

  if (me.channels.shopping?.withPrice > 0) facts.push({ claim: "shown_with_a_price", value: me.channels.shopping.withPrice, of });
  if (me.firstSeenAt) facts.push({ claim: "first_seen", at: me.firstSeenAt });
  return facts;
}
